import type {
  PhysioAdmRow,
  PhysioComparisonRow,
  PhysioFunctionalEvolutionRow,
  PhysioInitialResponses,
  PhysioMedicationRow,
  PhysioReEvaluationResponses,
  PhysioSkinfoldRow,
  PhysioStrengthRow,
  PhysiotherapyAssessmentType,
} from '../types/physiotherapyAssessment';
import { emptyPostureAssessment, normalizePostureAssessment } from './posturalAssessmentSchema';

export const PHYSIO_SPECIFIC_GOALS = [
  { key: 'pain_reduction', label: 'Redução da dor' },
  { key: 'strength_gain', label: 'Ganho de força' },
  { key: 'mobility_gain', label: 'Ganho de mobilidade' },
  { key: 'balance_improvement', label: 'Melhora do equilíbrio' },
  { key: 'posture_improvement', label: 'Melhora da postura' },
  { key: 'physical_conditioning', label: 'Condicionamento físico' },
  { key: 'injury_prevention', label: 'Prevenção de lesões' },
  { key: 'return_to_sports', label: 'Retorno às atividades físicas' },
  { key: 'adl_improvement', label: 'Melhora das AVDs' },
  { key: 'other', label: 'Outro' },
] as const;

export const PHYSIO_POSTURE_SEGMENTS = [
  { key: 'head_cervical', label: 'Cabeça/cervical' },
  { key: 'shoulders', label: 'Ombros' },
  { key: 'thoracic_spine', label: 'Coluna torácica' },
  { key: 'lumbar_spine', label: 'Coluna lombar' },
  { key: 'pelvis', label: 'Pelve' },
  { key: 'knees', label: 'Joelhos' },
  { key: 'feet', label: 'Pés' },
] as const;

export const PHYSIO_THERAPY_MODALITIES = [
  { key: 'pilates', label: 'Pilates' },
  { key: 'physiotherapy', label: 'Fisioterapia' },
  { key: 'rpg', label: 'RPG' },
  { key: 'therapeutic_exercise', label: 'Exercício terapêutico' },
  { key: 'strengthening', label: 'Fortalecimento' },
  { key: 'mobility', label: 'Mobilidade' },
  { key: 'balance', label: 'Equilíbrio' },
  { key: 'gait_training', label: 'Treino de marcha' },
  { key: 'manual_therapy', label: 'Terapia manual' },
  { key: 'other', label: 'Outro' },
] as const;

export const PHYSIO_CONDUCT_OPTIONS = [
  { key: 'maintain_plan', label: 'Manter plano' },
  { key: 'adjust_plan', label: 'Ajustar plano' },
  { key: 'intensify', label: 'Intensificar' },
  { key: 'reduce_frequency', label: 'Reduzir frequência' },
  { key: 'discharge', label: 'Alta' },
  { key: 'referral', label: 'Encaminhamento' },
] as const;

const DEFAULT_SKINFOLDS: PhysioSkinfoldRow[] = [
  { location: 'Tríceps', result_mm: '' },
  { location: 'Subescapular', result_mm: '' },
  { location: 'Supra-ilíaca', result_mm: '' },
  { location: 'Abdominal', result_mm: '' },
  { location: 'Coxa', result_mm: '' },
  { location: 'Panturrilha', result_mm: '' },
  { location: 'Outras', result_mm: '' },
];

const DEFAULT_MEDICATIONS: PhysioMedicationRow[] = Array.from({ length: 4 }, () => ({
  medication: '',
  dosage: '',
  frequency: '',
  purpose: '',
}));

const DEFAULT_ADM_ROWS: PhysioAdmRow[] = Array.from({ length: 4 }, () => ({
  joint_segment: '',
  movement: '',
  right: '',
  left: '',
  notes: '',
}));

const DEFAULT_STRENGTH_ROWS: PhysioStrengthRow[] = [
  { muscle_group: 'Extensores de joelho', right: '', left: '', notes: '' },
  { muscle_group: 'Flexores de joelho', right: '', left: '', notes: '' },
  { muscle_group: 'Extensores de quadril', right: '', left: '', notes: '' },
  { muscle_group: 'Flexores de quadril', right: '', left: '', notes: '' },
  { muscle_group: 'Abdutores de quadril', right: '', left: '', notes: '' },
  { muscle_group: 'Flexores plantares', right: '', left: '', notes: '' },
];

const DEFAULT_COMPARISON_ROWS: PhysioComparisonRow[] = [
  { indicator: 'Dor — 0–10', initial: '', current: '', evolution: '' },
  { indicator: 'ADM', initial: '', current: '', evolution: '' },
  { indicator: 'Força D', initial: '', current: '', evolution: '' },
  { indicator: 'Força E', initial: '', current: '', evolution: '' },
  { indicator: 'TUG — segundos', initial: '', current: '', evolution: '' },
  { indicator: 'Caminhada 4 m — segundos', initial: '', current: '', evolution: '' },
  { indicator: 'Velocidade da marcha — m/s', initial: '', current: '', evolution: '' },
  { indicator: 'Sentar/levantar 5x — segundos', initial: '', current: '', evolution: '' },
  { indicator: 'Degrau 2 min — repetições', initial: '', current: '', evolution: '' },
  { indicator: 'Equilíbrio unipodal D', initial: '', current: '', evolution: '' },
  { indicator: 'Equilíbrio unipodal E', initial: '', current: '', evolution: '' },
];

const DEFAULT_ANTHRO_COMPARISON: PhysioComparisonRow[] = [
  { indicator: 'Peso — kg', initial: '', current: '', evolution: '' },
  { indicator: 'IMC — kg/m²', initial: '', current: '', evolution: '' },
  { indicator: 'Circunferência abdominal — cm', initial: '', current: '', evolution: '' },
  { indicator: 'Circunferência da cintura — cm', initial: '', current: '', evolution: '' },
  { indicator: 'Circunferência do quadril — cm', initial: '', current: '', evolution: '' },
];

const DEFAULT_FUNCTIONAL_EVOLUTION: PhysioFunctionalEvolutionRow[] = Array.from({ length: 5 }, () => ({
  goal_activity: '',
  initial_assessment: '',
  reevaluation: '',
  status: '',
}));

export function emptyInitialResponses(): PhysioInitialResponses {
  return {
    identification: {
      full_name: '',
      birth_date: '',
      age: '',
      gender: '',
      profession: '',
      phone: '',
      weight_kg: '',
      height_m: '',
      imc: '',
      imc_classification: '',
    },
    anthropometry: {
      abdominal_circ_cm: '',
      waist_circ_cm: '',
      hip_circ_cm: '',
      skinfolds: DEFAULT_SKINFOLDS.map((r) => ({ ...r })),
      waist_hip_ratio: '',
      waist_hip_risk: '',
      waist_risk: '',
      skinfold_sum_mm: '',
      body_fat_percent: '',
      notes: '',
    },
    complaint: {
      main_complaint: '',
      main_goal: '',
      specific_goals: [],
      specific_goal_other: '',
      difficult_tasks: ['', '', '', '', ''],
    },
    clinical_history: {
      current_illness: '',
      past_history: '',
      prior_surgeries: '',
      prior_injuries: '',
      complementary_exams: '',
    },
    medications: DEFAULT_MEDICATIONS.map((r) => ({ ...r })),
    vital_signs: {
      blood_pressure: '',
      heart_rate: '',
      spo2: '',
      auscultation: '',
    },
    pain: {
      has_pain: '',
      intensity_end: '',
      location: '',
      characteristics: '',
      notes: '',
    },
    mobility: {
      general_adm: '',
      rows: DEFAULT_ADM_ROWS.map((r) => ({ ...r })),
    },
    strength: {
      rows: DEFAULT_STRENGTH_ROWS.map((r) => ({ ...r })),
    },
    coordination: {
      index_nose: '',
      prono_supino: '',
      lemocot: '',
    },
    balance: {
      open_base: '',
      romberg: '',
      tandem: '',
      unipodal_right: '',
      unipodal_left: '',
      tug_seconds: '',
    },
    gait: {
      walk_4m_seconds: '',
      gait_speed_ms: '',
      classification: '',
      analysis: '',
    },
    functional_tests: {
      sit_stand_5x_seconds: '',
      step_2min_reps: '',
    },
    posture: emptyPostureAssessment(),
    impression: {
      main_alterations: '',
      functional_limitations: '',
      risk_factors: '',
    },
    therapeutic_goals: {
      short_term: '',
      medium_term: '',
      long_term: '',
      functional_goals: '',
    },
    therapeutic_plan: {
      modalities: [],
      modality_other: '',
      frequency_per_week: '',
      guidance: '',
    },
  };
}

export function emptyReEvaluationResponses(): PhysioReEvaluationResponses {
  return {
    identification: {
      treatment_period: '',
      adherence: '',
      current_weight_kg: '',
      current_height_m: '',
      current_imc: '',
    },
    comparison: DEFAULT_COMPARISON_ROWS.map((r) => ({ ...r })),
    functional_evolution: DEFAULT_FUNCTIONAL_EVOLUTION.map((r) => ({ ...r })),
    anthropometry_comparison: DEFAULT_ANTHRO_COMPARISON.map((r) => ({ ...r })),
    conclusion: {
      clinical_evolution: '',
      main_gains: '',
      remaining_limitations: '',
      achieved_goals: '',
      new_goals: '',
      conduct: [],
      notes: '',
    },
  };
}

export function emptyResponsesForType(type: PhysiotherapyAssessmentType) {
  return type === 're_evaluation' ? emptyReEvaluationResponses() : emptyInitialResponses();
}

export function parseNumericInput(raw: string): number | null {
  const v = parseFloat(String(raw ?? '').replace(',', '.').trim());
  return Number.isFinite(v) ? v : null;
}

export function computeImc(weightKg: string, heightM: string): string {
  const w = parseNumericInput(weightKg);
  const h = parseNumericInput(heightM);
  if (w == null || h == null || h <= 0) return '';
  const imc = w / (h * h);
  if (!Number.isFinite(imc)) return '';
  return imc.toFixed(1);
}

export function computeImcClassification(imc: string): string {
  const v = parseNumericInput(imc);
  if (v == null) return '';
  if (v < 18.5) return 'Baixo peso';
  if (v < 25) return 'Eutrofia';
  if (v < 30) return 'Sobrepeso';
  if (v < 35) return 'Obesidade grau I';
  if (v < 40) return 'Obesidade grau II';
  return 'Obesidade grau III';
}

export function computeWaistHipRatio(waistCm: string, hipCm: string): string {
  const waist = parseNumericInput(waistCm);
  const hip = parseNumericInput(hipCm);
  if (waist == null || hip == null || hip <= 0) return '';
  const ratio = waist / hip;
  if (!Number.isFinite(ratio)) return '';
  return ratio.toFixed(2);
}

export function computeWaistHipRisk(ratio: string, gender: 'M' | 'F' | ''): string {
  const v = parseNumericInput(ratio);
  if (v == null || !gender) return '';
  if (gender === 'M') {
    if (v < 0.9) return 'Baixo risco';
    if (v < 1.0) return 'Risco moderado';
    return 'Alto risco';
  }
  if (v < 0.8) return 'Baixo risco';
  if (v < 0.85) return 'Risco moderado';
  return 'Alto risco';
}

export function computeWaistRisk(waistCm: string, gender: 'M' | 'F' | ''): string {
  const waist = parseNumericInput(waistCm);
  if (waist == null || !gender) return '';
  if (gender === 'M') {
    if (waist < 94) return 'Baixo risco metabólico';
    if (waist < 102) return 'Risco moderado';
    return 'Alto risco metabólico';
  }
  if (waist < 80) return 'Baixo risco metabólico';
  if (waist < 88) return 'Risco moderado';
  return 'Alto risco metabólico';
}

export function computeSkinfoldSum(skinfolds: PhysioSkinfoldRow[]): string {
  let sum = 0;
  let count = 0;
  for (const row of skinfolds) {
    const v = parseNumericInput(row.result_mm);
    if (v != null) {
      sum += v;
      count += 1;
    }
  }
  if (!count) return '';
  return sum.toFixed(1);
}

function skinfoldByLocation(skinfolds: PhysioSkinfoldRow[], location: string): number | null {
  const row = skinfolds.find((s) => s.location.toLowerCase() === location.toLowerCase());
  return row ? parseNumericInput(row.result_mm) : null;
}

/** Estimativa % GC — Jackson & Pollock 3 dobras (Siri). */
export function computeBodyFatPercent(
  skinfolds: PhysioSkinfoldRow[],
  ageRaw: string,
  gender: 'M' | 'F' | '',
): string {
  const age = parseNumericInput(ageRaw);
  if (age == null || !gender) return '';

  let sum3: number | null = null;
  if (gender === 'F') {
    const triceps = skinfoldByLocation(skinfolds, 'Tríceps');
    const suprailiac = skinfoldByLocation(skinfolds, 'Supra-ilíaca');
    const thigh = skinfoldByLocation(skinfolds, 'Coxa');
    if (triceps == null || suprailiac == null || thigh == null) return '';
    sum3 = triceps + suprailiac + thigh;
  } else {
    const abdominal = skinfoldByLocation(skinfolds, 'Abdominal');
    const triceps = skinfoldByLocation(skinfolds, 'Tríceps');
    const thigh = skinfoldByLocation(skinfolds, 'Coxa');
    if (abdominal == null || triceps == null || thigh == null) return '';
    sum3 = abdominal + triceps + thigh;
  }

  const density = gender === 'F'
    ? 1.0994921 - (0.0009929 * sum3) + (0.0000023 * sum3 * sum3) - (0.0001392 * age)
    : 1.10938 - (0.0008267 * sum3) + (0.0000016 * sum3 * sum3) - (0.0002574 * age);

  if (!Number.isFinite(density) || density <= 0) return '';
  const bodyFat = (495 / density) - 450;
  if (!Number.isFinite(bodyFat)) return '';
  return Math.max(0, Math.min(60, bodyFat)).toFixed(1);
}

export function computeComparisonEvolution(initial: string, current: string): string {
  const a = parseNumericInput(initial);
  const b = parseNumericInput(current);
  if (a == null || b == null) return '';
  const delta = b - a;
  const sign = delta > 0 ? '+' : '';
  return `${sign}${delta.toFixed(1).replace(/\.0$/, '')}`;
}

export function recomputeIdentificationDerived(
  identification: PhysioInitialResponses['identification'],
): PhysioInitialResponses['identification'] {
  const imc = computeImc(identification.weight_kg, identification.height_m);
  return {
    ...identification,
    imc,
    imc_classification: computeImcClassification(imc),
  };
}

export function recomputeAnthropometryDerived(
  anthropometry: PhysioInitialResponses['anthropometry'],
  gender: 'M' | 'F' | '',
  age: string,
): PhysioInitialResponses['anthropometry'] {
  const waist_hip_ratio = computeWaistHipRatio(anthropometry.waist_circ_cm, anthropometry.hip_circ_cm);
  return {
    ...anthropometry,
    waist_hip_ratio,
    waist_hip_risk: computeWaistHipRisk(waist_hip_ratio, gender),
    waist_risk: computeWaistRisk(anthropometry.waist_circ_cm, gender),
    skinfold_sum_mm: computeSkinfoldSum(anthropometry.skinfolds),
    body_fat_percent: computeBodyFatPercent(anthropometry.skinfolds, age, gender),
  };
}

export function recomputeComparisonRows(
  rows: PhysioComparisonRow[],
): PhysioComparisonRow[] {
  return rows.map((row) => ({
    ...row,
    evolution: computeComparisonEvolution(row.initial, row.current),
  }));
}

function patchComparisonIndicator(
  rows: PhysioComparisonRow[],
  indicator: string,
  patch: Partial<PhysioComparisonRow>,
): PhysioComparisonRow[] {
  const idx = rows.findIndex((r) => r.indicator === indicator);
  if (idx < 0) return rows;
  const next = [...rows];
  next[idx] = { ...next[idx], ...patch };
  return next;
}

export function applyReEvalAnthropometryCurrent(
  responses: PhysioReEvaluationResponses,
): PhysioReEvaluationResponses {
  const { current_weight_kg, current_height_m } = responses.identification;
  const current_imc = computeImc(current_weight_kg, current_height_m);
  let anthropometry_comparison = patchComparisonIndicator(
    responses.anthropometry_comparison,
    'Peso — kg',
    { current: current_weight_kg },
  );
  anthropometry_comparison = patchComparisonIndicator(
    anthropometry_comparison,
    'IMC — kg/m²',
    { current: current_imc },
  );
  return {
    ...responses,
    identification: { ...responses.identification, current_imc },
    anthropometry_comparison: recomputeComparisonRows(anthropometry_comparison),
  };
}

export function prefillReEvaluationFromPrior(
  responses: PhysioReEvaluationResponses,
  prior: PhysioInitialResponses,
): PhysioReEvaluationResponses {
  const id = prior.identification;
  const ant = prior.anthropometry;
  let anthropometry_comparison = responses.anthropometry_comparison;
  anthropometry_comparison = patchComparisonIndicator(anthropometry_comparison, 'Peso — kg', { initial: id.weight_kg });
  anthropometry_comparison = patchComparisonIndicator(anthropometry_comparison, 'IMC — kg/m²', { initial: id.imc });
  anthropometry_comparison = patchComparisonIndicator(
    anthropometry_comparison,
    'Circunferência abdominal — cm',
    { initial: ant.abdominal_circ_cm },
  );
  anthropometry_comparison = patchComparisonIndicator(
    anthropometry_comparison,
    'Circunferência da cintura — cm',
    { initial: ant.waist_circ_cm },
  );
  anthropometry_comparison = patchComparisonIndicator(
    anthropometry_comparison,
    'Circunferência do quadril — cm',
    { initial: ant.hip_circ_cm },
  );
  return applyReEvalAnthropometryCurrent({
    ...responses,
    identification: {
      ...responses.identification,
      current_height_m: id.height_m,
    },
    anthropometry_comparison: recomputeComparisonRows(anthropometry_comparison),
  });
}

export function computeAgeFromBirthDate(birthDate: string): string {
  if (!birthDate) return '';
  const birth = new Date(`${birthDate}T12:00:00`);
  if (Number.isNaN(birth.getTime())) return '';
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age -= 1;
  return age >= 0 ? String(age) : '';
}

export function prefillInitialFromPatient(
  responses: PhysioInitialResponses,
  patient: any,
): PhysioInitialResponses {
  const genderMap: Record<string, 'M' | 'F' | ''> = {
    masculine: 'M',
    feminine: 'F',
  };
  const birth = patient?.birth_date || '';
  const age = computeAgeFromBirthDate(birth);
  const identification = recomputeIdentificationDerived({
    ...responses.identification,
    full_name: patient?.full_name || responses.identification.full_name,
    birth_date: birth || responses.identification.birth_date,
    age: age || responses.identification.age,
    gender: genderMap[patient?.gender] || responses.identification.gender,
    profession: patient?.profession || responses.identification.profession,
    phone: patient?.whatsapp || patient?.phone || responses.identification.phone,
  });
  const anthropometry = recomputeAnthropometryDerived(
    responses.anthropometry,
    identification.gender,
    identification.age,
  );
  return { ...responses, identification, anthropometry };
}

export const PHYSIO_INITIAL_SECTIONS = [
  { key: 'identification', title: '1. Identificação' },
  { key: 'anthropometry', title: '2. Avaliação antropométrica (opcional)' },
  { key: 'complaint', title: '3. Queixa e objetivos' },
  { key: 'clinical_history', title: '4. História clínica' },
  { key: 'medications', title: '5. Medicamentos' },
  { key: 'vital_signs', title: '6. Sinais vitais' },
  { key: 'pain', title: '7. Dor' },
  { key: 'mobility', title: '8. Mobilidade / ADM' },
  { key: 'strength', title: '9. Força muscular' },
  { key: 'coordination', title: '10. Coordenação' },
  { key: 'balance', title: '11. Equilíbrio' },
  { key: 'gait', title: '12. Marcha' },
  { key: 'functional_tests', title: '13. Testes funcionais' },
  { key: 'posture', title: '14. Avaliação postural' },
  { key: 'impression', title: '15. Impressão fisioterapêutica' },
  { key: 'therapeutic_goals', title: '16. Objetivos terapêuticos' },
  { key: 'therapeutic_plan', title: '17. Plano terapêutico' },
] as const;

export const PHYSIO_REEVAL_SECTIONS = [
  { key: 'identification', title: '1. Identificação da reavaliação' },
  { key: 'comparison', title: '2. Comparação dos resultados' },
  { key: 'functional_evolution', title: '3. Evolução funcional' },
  { key: 'anthropometry_comparison', title: '4. Avaliação antropométrica (se utilizada)' },
  { key: 'conclusion', title: '5. Conclusão da reavaliação' },
] as const;
